// src/components/layout/layout.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Navbar } from '../Navbar';
import { useLanguage } from '@/hooks/useLanguage';
import { useResponsive } from '@/hooks/useResponsive';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useNotifications } from '@/hooks/useNotifications';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { AuthDialog } from '../auth/auth-dialog';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { NAVIGATION_ITEMS, NAVIGATION_CATEGORIES } from '@/lib/config/navigation';
import { prefetchRoute } from '@/routes';
import { 
  Home, Users, MessageCircle, Bell, User, Store, 
  Calendar, Music2, Wallet, GraduationCap, Settings,
  UtensilsCrossed, Gamepad2, Bot, BarChart3, Zap,
  HeartHandshake, Gift, Plane, Lock, HelpCircle,
  UsersRound, Globe, Ticket, ScrollText, Tag, Briefcase,
  ShoppingBasket, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { CommandPalette } from '../ui/command-palette';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [exchangeFullscreen, setExchangeFullscreen] = useState(false);
  const activeNavigationItemRef = useRef<HTMLButtonElement>(null);
  const { isRTL } = useLanguage();
  const { isMobile } = useResponsive();
  const { user, loading, userData } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isExchangeRoute = location.pathname === '/exchange';
  const isExchangeFullscreen = !isMobile && isExchangeRoute && exchangeFullscreen;
  const sidebarIsOverlay = isMobile || isExchangeFullscreen;
  const sidebarHidden = sidebarIsOverlay && !sidebarOpen;

  useEffect(() => {
    if (isMobile) return;

    const handleExchangeFullscreen = (event: Event) => {
      const custom = event as CustomEvent<{ enabled?: boolean }>;
      const enabled = !!custom.detail?.enabled;

      if (!isExchangeRoute) {
        setExchangeFullscreen(false);
        return;
      }

      if (enabled) {
        setExchangeFullscreen(true);
        setSidebarOpen(false);
        return;
      }

      setExchangeFullscreen(false);
      setSidebarCollapsed(false);
    };

    window.addEventListener('intouch:exchange-fullscreen', handleExchangeFullscreen as EventListener);

    return () => {
      window.removeEventListener('intouch:exchange-fullscreen', handleExchangeFullscreen as EventListener);
    };
  }, [isExchangeRoute, isMobile]);

  useEffect(() => {
    if (isMobile) return;
    if (isExchangeRoute) return;

    setExchangeFullscreen(false);
    setSidebarCollapsed(false);
  }, [isExchangeRoute, isMobile]);

  const normalizedAuthEmail = (user?.email || '').trim().toLowerCase();
  const normalizedProfileEmail = (userData?.email || '').trim().toLowerCase();
  const isPrivilegedEmail = normalizedAuthEmail === 'milan.krnjajic@gmail.com' || normalizedProfileEmail === 'milan.krnjajic@gmail.com';
  const isSuperAdmin = isPrivilegedEmail || userData?.role === 'superadmin';
  const isAdminUser = isSuperAdmin || !!userData?.isAdmin || userData?.role === 'admin';
  const isSellerUser = !!userData?.isSeller || userData?.role === 'seller' || userData?.role === 'superadmin';

  useEffect(() => {
    if (loading || sidebarHidden) return;

    activeNavigationItemRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [location.pathname, sidebarHidden, sidebarCollapsed, loading, isSuperAdmin, isAdminUser, isSellerUser]);

  // Get filtered navigation items from external config
  const getFilteredNavigationItems = () => {
    return NAVIGATION_ITEMS.filter(item => {
      // Filter based on user permissions
      if (item.superAdminOnly && !isSuperAdmin) return false;
      if (item.adminOnly && !isAdminUser) return false;
      if (item.sellerOnly && !isSellerUser) return false;
      return true;
    });
  };

  const allNavigationItems = getFilteredNavigationItems();

  // Group items by category using external config
  const getNavigationByCategory = () => {
    const navigationByCategory: Record<string, typeof allNavigationItems> = {};
    
    Object.entries(NAVIGATION_CATEGORIES).forEach(([category, labels]) => {
      const items = labels
        .map(label => allNavigationItems.find(item => item.label === label))
        .filter((item): item is NonNullable<typeof item> => item !== undefined);
      
      if (items.length > 0) {
        navigationByCategory[category] = items;
      }
    });
    
    return navigationByCategory;
  };

  const navigationByCategory = getNavigationByCategory();

  const { showPalette, setShowPalette, paletteQuery, setPaletteQuery, filteredItems, favoriteCommandIds, toggleFavoriteCommand } = useKeyboardShortcuts({
    currentPath: location.pathname,
    prefetchPath: prefetchRoute,
    isSuperAdmin,
    isAdminUser,
    isSellerUser,
  });

  const toggleSidebar = () => {
    if (sidebarIsOverlay) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  // Function to get translations for navigation items
  const getItemTranslation = (item: any) => {
    // Map item labels to translation keys
    const translationKeyMap: { [key: string]: string } = {
      'Home': 'sidebar.home',
      'Feed': 'sidebar.feed',
      'Profile': 'sidebar.profile',
      'Pets': 'sidebar.pets',
      'Messages': 'sidebar.messages',
      'Notifications': 'sidebar.notifications',
      'Friends': 'sidebar.friends',
      'Groups': 'sidebar.groups',
      'Pages': 'sidebar.pages',
      'Events': 'sidebar.events',
      'Music': 'sidebar.music',
      'Marketplace': 'sidebar.marketplace',
      'My Products': 'sidebar.myProducts',
      'Bookings': 'sidebar.bookings',
      'Food': 'sidebar.food',
      'Recipes': 'sidebar.recipes',
      'Transportation': 'sidebar.transportation',
      'Tickets': 'sidebar.tickets',
      'Classifieds': 'sidebar.classifieds',
      'Coupons': 'sidebar.coupons',
      'Boost': 'sidebar.boost',
      'Freelance': 'sidebar.freelance',
      'Groceries': 'sidebar.groceries',
      'Learning': 'sidebar.learning',
      'Games': 'sidebar.games',
      'AI Assistant': 'sidebar.ai',
      'Wallet': 'sidebar.wallet',
      'Accessibility Settings': 'sidebar.accessibility',
      'Credit Card': 'sidebar.creditCard',
      'Crypto Atlas': 'sidebar.cryptoAtlas',
      'Exchange': 'sidebar.exchange',
      'Mining': 'sidebar.mining',
      'Staking': 'sidebar.staking',
      'Charity': 'sidebar.charity',
      'Crowdfunding': 'sidebar.crowdfunding',
      'Rewards': 'sidebar.rewards',
      'Flights': 'sidebar.flights',
      'Trains': 'sidebar.trains',
      'Buses': 'sidebar.buses',
      'Ferries': 'sidebar.ferries',
      'Taxi': 'sidebar.taxi',
      'Super Admin': 'sidebar.superAdmin',
      'Telemetry Ops': 'sidebar.telemetryOps',
      'Debug Panel': 'sidebar.debugPanel',
      'Admin Dashboard': 'sidebar.adminDashboard',
      'Admin Mining': 'sidebar.adminMining',
      'Seller Dashboard': 'sidebar.sellerDashboard',
      'My Orders': 'sidebar.myOrders',
      'Sales Analytics': 'sidebar.salesAnalytics',
      'Settings': 'sidebar.settings',
      'Privacy Settings': 'sidebar.privacySettings',
      'KYC Verification': 'sidebar.kycVerification',
      'Help Center': 'sidebar.helpCenter',
      'Documentation': 'sidebar.documentation'
    };

    // Return translation using i18n or fallback to original
    const translationKey = translationKeyMap[item.label];
    return translationKey ? t(translationKey, item.label) : item.label;
  };

  // Function to get category translations
  const getCategoryTranslation = (category: string) => {
    // Map categories to translation keys
    const categoryTranslationKeys: { [key: string]: string } = {
      'main': 'navigation.categories.main',
      'content': 'navigation.categories.content',
      'services': 'navigation.categories.services',
      'entertainment': 'navigation.categories.entertainment',
      'finance': 'navigation.categories.finance',
      'charity': 'navigation.categories.charity',
      'transport': 'navigation.categories.transport',
      'business': 'navigation.categories.business',
      'support': 'navigation.categories.support'
    };

    // Return translation using i18n or fallback to uppercase original
    const translationKey = categoryTranslationKeys[category];
    return translationKey ? t(translationKey, category.toUpperCase()).toUpperCase() : category.toUpperCase();
  };

  // Loading state: only block while auth state is unknown.
  // Once auth resolves, render immediately even if userData is still hydrating.
  if (loading) {
    return (
      <div className="dashboard-shell min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="text-slate-600 dark:text-slate-300 font-medium">Loading InTouch...</span>
        </div>
      </div>
    );
  }

  const sidebar = (
    <aside
      className={cn(
        "sidebar border-r border-slate-200/70 bg-white/84 backdrop-blur-2xl transition-all duration-300 ease-in-out opacity-100 dark:border-slate-700/80 dark:bg-slate-900/74",
        "fixed top-16 bottom-0 left-0 flex flex-col overflow-hidden z-40",
        sidebarCollapsed ? "w-24" : "w-64",
        sidebarHidden ? "-translate-x-full invisible pointer-events-none" : "translate-x-0",
        sidebarIsOverlay && "shadow-2xl"
      )}
      aria-label="Main navigation"
      aria-hidden={sidebarHidden}
      role="navigation"
      id="sidebar-nav"
    >
      {/* Header */}
      <div className="flex items-center justify-end border-b border-slate-200/70 p-2 dark:border-slate-700/80">
        {sidebarIsOverlay && (
          <Button
            variant="ghost"
            size="sm"
            onClick={closeSidebar}
            className="p-2 rounded-xl text-blue-700 hover:bg-blue-100 dark:text-blue-200 dark:hover:bg-blue-500/20"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 overscroll-contain" aria-label="Navigation menu">
        <div className="space-y-3">
          {Object.entries(navigationByCategory).map(([category, items]) => (
            <div key={category}>
              {/* Category header */}
              <h3
                className={cn(
                  "py-1 font-semibold uppercase text-blue-500/75 dark:text-blue-200/70",
                  sidebarCollapsed
                    ? "px-2 text-center text-[10px] leading-tight tracking-[0.12em]"
                    : "px-3 text-[11px] tracking-[0.16em]"
                )}
              >
                {getCategoryTranslation(category)}
              </h3>
              {/* Category items */}
              <div className="space-y-1 px-2">
                {items.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  
                  // Dynamically add notification badge
                  const badge = item.label === 'Notifications' && unreadCount > 0 ? unreadCount : ('badge' in item ? item.badge : undefined);
                  
                  return (
                    <button
                      key={item.path}
                      ref={isActive ? activeNavigationItemRef : null}
                      title={sidebarCollapsed ? getItemTranslation(item) : undefined}
                      aria-label={sidebarCollapsed ? getItemTranslation(item) : undefined}
                      onClick={() => handleNavigation(item.path)}
                      onMouseEnter={() => prefetchRoute(item.path)}
                      onFocus={() => prefetchRoute(item.path)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-blue-400/70 focus:ring-offset-2",
                        isActive 
                          ? "bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600 text-white shadow-[0_16px_28px_-16px_rgba(37,99,235,0.9)]" 
                          : "text-slate-700 hover:bg-blue-50/90 hover:text-blue-800 bg-transparent dark:text-blue-200 dark:hover:bg-blue-500/15",
                        sidebarCollapsed && "justify-center px-2"
                      )}
                      aria-current={isActive ? "page" : undefined}
                      role="menuitem"
                    >
                      <Icon className={cn(
                        "w-5 h-5 flex-shrink-0 stroke-2",
                        isActive ? "text-white stroke-white fill-none" : "text-blue-500 stroke-blue-500 fill-none"
                      )} />
                      {!sidebarCollapsed && (
                        <>
                          <span className={cn(
                            "flex-1 text-left font-medium",
                            isActive ? "text-white" : "text-slate-700 dark:text-blue-200"
                          )}>{getItemTranslation(item)}</span>
                          {badge && (
                            <span 
                              className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[1.25rem] h-5 flex items-center justify-center font-semibold"
                              aria-label={`${badge} unread`}
                            >
                              {String(badge)}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </nav>
    </aside>
  );

  // Main app layout
  return (
    <div className="dashboard-shell min-h-screen bg-transparent">
        <Navbar
          onMenuClick={toggleSidebar}
          onNavigate={handleNavigation}
          showDesktopMenu={isExchangeFullscreen}
          menuExpanded={sidebarIsOverlay ? sidebarOpen : !sidebarCollapsed}
        />
        
        {/* Sidebar overlay backdrop */}
        {sidebarIsOverlay && sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 transition-opacity duration-300 backdrop-blur-sm"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        )}
        
        {sidebar}
        
        {/* Command Palette (global: Ctrl+K) */}
        <CommandPalette
          isOpen={showPalette}
          onClose={() => setShowPalette(false)}
          query={paletteQuery}
          onQueryChange={setPaletteQuery}
          items={filteredItems}
          onSelect={(item) => item.action()}
          favoriteCommandIds={favoriteCommandIds}
          onToggleFavorite={toggleFavoriteCommand}
        />

        <main className={cn(
          "relative z-0 min-h-screen overflow-x-hidden px-2 pb-6 transition-all duration-300 sm:px-3 lg:px-4",
          isExchangeFullscreen && "ml-0",
          !isMobile && !isExchangeFullscreen && !sidebarCollapsed && "ml-64",
          !isMobile && !isExchangeFullscreen && sidebarCollapsed && "ml-24",
          isMobile && "ml-0"
        )}>
          <div className="w-full pt-16">
            <div className={cn(
              "animate-fade-in w-full mx-auto",
              isExchangeRoute && exchangeFullscreen ? "max-w-none" : "max-w-[1520px]"
            )}>
              {!user ? (
                <div className="max-w-md mx-auto mt-20 px-4 sm:px-6">
                  <div className="rounded-3xl border border-slate-200/75 bg-white/88 p-6 shadow-[0_24px_56px_-30px_rgba(15,23,42,0.42)] backdrop-blur-xl dark:border-slate-700/80 dark:bg-slate-900/80 sm:p-8">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <svg className="w-8 h-8 text-white fill-current" viewBox="0 0 24 24">
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                        </svg>
                      </div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Welcome to InTouch</h1>
                      <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300">Connect with friends and share your moments</p>
                    </div>
                    <AuthDialog 
                      isOpen={true} 
                      onClose={() => {}} 
                    />
                  </div>
                </div>
              ) : (
                children
              )}
            </div>
          </div>
        </main>
        
    </div>
  );
}